import { NavLink } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard, Building2, MapPin, Users, ClipboardList, KanbanSquare,
  LifeBuoy, Wallet, GraduationCap, BarChart3, Settings, ChevronLeft, ChevronRight, X, Ruler,
} from "lucide-react";

const nav = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: "*" },
  { to: "/solicitacoes", label: "Solicitações", icon: ClipboardList, roles: ["admin_geral","administrativo","financeiro","comercial","supervisor","vistoriador","imobiliaria_admin","imobiliaria_user"] },
  { to: "/agenda", label: "Agenda", icon: Ruler, roles: ["admin_geral","administrativo","comercial","supervisor","imobiliaria_admin","imobiliaria_user"] },
  { to: "/faturamento", label: "Financeiro", icon: Wallet, roles: ["admin_geral","administrativo","financeiro","comercial","imobiliaria_admin","imobiliaria_user"] },
  { to: "/cidades", label: "Cidades", icon: MapPin, roles: ["admin_geral"] },
  { to: "/imobiliarias", label: "Imobiliárias", icon: Building2, roles: ["admin_geral","administrativo","financeiro","comercial","supervisor","imobiliaria_admin","imobiliaria_user"] },
  { to: "/usuarios", label: "Usuários", icon: Users, roles: ["admin_geral","administrativo","imobiliaria_admin"] },
];

const futureNav = [
  { label: "Kanban", icon: KanbanSquare },
  { label: "Suporte", icon: LifeBuoy },
  { label: "Cursos", icon: GraduationCap },
  { label: "Relatórios", icon: BarChart3 },
  { label: "Configurações", icon: Settings },
];

// tiny helper for testids without accents
const slug = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

export default function Sidebar({ collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const { user } = useAuth();
  const canSee = (roles) => roles === "*" || roles.includes(user?.role);

  const width = collapsed ? "w-16" : "w-64";

  const content = (
    <>
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        {!collapsed ? (
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded bg-[#FFD400] flex items-center justify-center font-display font-black text-[#0B1F3A]">P</div>
            <div>
              <div className="font-display font-bold text-sm leading-none text-white">PRUMO</div>
              <div className="text-[10px] uppercase tracking-widest text-white/50">Gestão</div>
            </div>
          </div>
        ) : (
          <div className="w-8 h-8 mx-auto rounded bg-[#FFD400] flex items-center justify-center font-display font-black text-[#0B1F3A]">P</div>
        )}
        <button
          className="ml-auto hidden lg:block text-white/50 hover:text-white"
          onClick={() => setCollapsed(!collapsed)}
          data-testid="sidebar-collapse-toggle"
        >
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
        <button
          className="ml-auto lg:hidden text-white/50 hover:text-white"
          onClick={() => setMobileOpen(false)}
          data-testid="sidebar-mobile-close"
        >
          <X size={20} />
        </button>
      </div>

      <nav className="flex-1 overflow-y-auto py-4">
        <div className={`px-4 pb-2 text-[10px] uppercase tracking-[0.2em] text-white/40 ${collapsed ? "hidden" : ""}`}>Operação</div>
        {nav.filter((n) => canSee(n.roles)).map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.to === "/"}
            data-testid={`sidebar-link-${slug(n.label)}`}
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-2.5 text-sm text-white/70 hover:text-white hover:bg-white/5 transition-colors ${isActive ? "side-active" : ""}`
            }
          >
            <n.icon size={18} className="shrink-0" />
            {!collapsed && <span className="truncate">{n.to === "/solicitacoes" && user?.role === "vistoriador" ? "Minhas Vistorias" : n.label}</span>}
          </NavLink>
        ))}

        {!collapsed && (
          <>
            <div className="px-4 pt-6 pb-2 text-[10px] uppercase tracking-[0.2em] text-white/40">Próximas fases</div>
            {futureNav.map((n) => (
              <div key={n.label} className="flex items-center gap-3 px-4 py-2.5 text-sm text-white/30 cursor-not-allowed">
                <n.icon size={18} className="shrink-0" />
                <span className="truncate">{n.label}</span>
                <span className="ml-auto text-[9px] uppercase tracking-widest text-white/20">em breve</span>
              </div>
            ))}
          </>
        )}
      </nav>

      <div className={`p-4 border-t border-white/10 ${collapsed ? "text-center" : ""}`}>
        {!collapsed ? (
          <div className="text-[10px] text-white/40 uppercase tracking-widest">Fase 2 · Operação</div>
        ) : (
          <div className="w-2 h-2 mx-auto rounded-full bg-[#FFD400]" />
        )}
      </div>
    </>
  );

  return (
    <>
      <aside className={`hidden lg:flex flex-col ${width} shrink-0 border-r border-black/10 bg-[#0B1F3A] transition-all duration-300`}>
        {content}
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-40" data-testid="mobile-sidebar-overlay">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="absolute left-0 top-0 bottom-0 w-64 bg-[#0B1F3A] border-r border-white/10 flex flex-col">
            {content}
          </aside>
        </div>
      )}
    </>
  );
}
